import React, { ChangeEvent, useState } from "react";
import { Form, Input, Checkbox, Button, CheckboxChangeEvent, Typography } from "antd";
import { SchoolEntityInput } from "../../../../services/schools/school.types";
import { SchoolDiaryType } from "../../../../types/enums";
import { CoordinatorInput } from "../../../../services/classrooms/classrooms.types";
import SchoolProgramsSelect from "./SchoolProgramsSelect";
import ManipulativesSwitches from "./ManipulativesSwitches";
import { ClassEntity } from "../types";
import {
  ABBREVIATION_TAKEN_MESSAGE,
  AbbreviationOwner,
  findAbbreviationOwner,
  normalizeAbbreviation,
} from "../utils";

const { Title, Text } = Typography;

interface SchoolTabProps {
  initialValues: SchoolEntityInput;
  setInitialValues: React.Dispatch<React.SetStateAction<SchoolEntityInput>>;
  coordinator: CoordinatorInput | undefined;
  setCoordinator: React.Dispatch<
    React.SetStateAction<CoordinatorInput | undefined>
  >;
  onFinish: () => void;
  loading: boolean;
  // True once a school id exists (route or in-session created), so the Save
  // button reads "Actualizar" instead of "Guardar" on re-save.
  isUpdate?: boolean;
  // Programas multiselect (edit mode only, replaces the dedicated Programas
  // tab) — only rendered when isUpdate is true.
  licensedProgramIds?: number[];
  classes?: ClassEntity[];
  onCommitPrograms?: (newProgramIds: number[]) => Promise<void> | void;
  // SCRUM-922: los niveles que la escuela realmente tiene contratados. Sólo
  // esos muestran interruptores de manipulativos. Ambos por defecto: al crear
  // una escuela todavía no hay programas licenciados de donde deducirlo, y
  // esconder los cuatro sería peor que mostrarlos de más.
  hasKinder?: boolean;
  hasPrimaria?: boolean;
  // SCRUM-916: esta escuela es un plantel de otra. Toda la configuración
  // compartida (bitácora, demo, programas, manipulativos) se administra en la
  // escuela principal — el backend responde 400 CAMPUS_CONFIG_READONLY si un
  // PATCH de plantel la trae —, así que acá queda sólo la identidad del
  // plantel y el CCT deja de ser obligatorio (un plantel puede no tener uno).
  isCampus?: boolean;
  // SCRUM-1034: las demás escuelas vivas (principales y planteles, sin la que
  // se está editando). La abreviatura se valida en el campo contra ellas con
  // el mismo criterio del backend: sin distinguir mayúsculas ni espacios al
  // borde. Vacío => queda sólo la validación del backend (409, mismo aviso).
  otherSchools?: AbbreviationOwner[];
  // SCRUM-1034: la abreviatura con la que se cargó la escuela. Conservarla no
  // se valida — mismo criterio que el backend y el trigger —, así una
  // duplicada heredada (el par "Happy" de prod) sigue pudiendo editar el resto
  // de sus datos. Vacío en el alta, donde todo valor cuenta como nuevo.
  originalAbbreviation?: string;
  // Injection slot rendered inside the form, right before the submit button.
  // Creation mode uses it for the upfront class-generation section.
  children?: React.ReactNode;
}

const SchoolTab: React.FC<SchoolTabProps> = ({
  initialValues,
  setInitialValues,
  coordinator,
  setCoordinator,
  onFinish,
  loading,
  isUpdate = false,
  licensedProgramIds = [],
  classes = [],
  onCommitPrograms = async () => {},
  hasKinder = true,
  hasPrimaria = true,
  isCampus = false,
  otherSchools = [],
  originalAbbreviation = "",
  children,
}) => {
  const [coordinatorEmailTouched, setCoordinatorEmailTouched] = useState(false);

  const handleCoordinatorChange = (
    field: keyof CoordinatorInput,
    value: string
  ) => {
    setCoordinator((prev) => ({
      first_name: prev?.first_name ?? "",
      last_name: prev?.last_name ?? "",
      email: prev?.email ?? "",
      [field]: value,
    }));
  };

  const coordinatorEmailInvalid =
    coordinatorEmailTouched &&
    !!coordinator?.email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(coordinator.email);

  return (
    <Form initialValues={initialValues} layout="vertical" onFinish={onFinish}>
      <Form.Item
        label="CCT"
        name="cct"
        rules={[{ required: !isCampus, message: "El CCT es obligatorio" }]}
        extra={
          isCampus ? "Dejalo vacío si el plantel no tiene CCT propio." : undefined
        }
      >
        <Input
          placeholder="Ingrese el CCT"
          value={initialValues.cct}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setInitialValues({
              ...initialValues,
              cct: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="Nombre"
        name="name"
        rules={[{ required: true, message: "El nombre es obligatorio" }]}
      >
        <Input
          placeholder="Ingrese el nombre de la escuela"
          value={initialValues.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setInitialValues({
              ...initialValues,
              name: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="Abreviatura"
        name="abbreviation"
        rules={[
          { required: true, message: "La abreviatura es obligatoria" },
          {
            validator: (_rule, value: string) => {
              const keepsOriginal =
                normalizeAbbreviation(originalAbbreviation) !== "" &&
                normalizeAbbreviation(value) ===
                  normalizeAbbreviation(originalAbbreviation);
              return !keepsOriginal && findAbbreviationOwner(value, otherSchools)
                ? Promise.reject(new Error(ABBREVIATION_TAKEN_MESSAGE))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input
          placeholder="Ej. ESC1, PRIM01, SEC02"
          value={initialValues.abbreviation}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setInitialValues({
              ...initialValues,
              abbreviation: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="Ciudad"
        name="city"
        rules={[{ required: true, message: "La ciudad es obligatoria" }]}
      >
        <Input
          placeholder="Ingrese la ciudad"
          value={initialValues.city}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setInitialValues({
              ...initialValues,
              city: e.target.value,
            })
          }
        />
      </Form.Item>

      {/* SCRUM-916: bitácora, bitácora de Primaria y Escuela Demo son
          configuración de la escuela principal. En un plantel no se muestran
          — y buildSchoolScalarPayload tampoco las manda. */}
      {!isCampus && (
        <>
      {/* Inline exclusive checkboxes per Figma (node 9943-97411), not a Select.
          Driven by state (not the Form store) so async edit-mode hydration
          reflects the value the backend returned. */}
      <Form.Item label="Bitácora escolar">
        <Checkbox
          checked={initialValues.diary_type === SchoolDiaryType.FULL_DAY}
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setInitialValues({
                ...initialValues,
                diary_type: SchoolDiaryType.FULL_DAY,
              });
            }
          }}
        >
          Por día completo
        </Checkbox>
        <Checkbox
          checked={initialValues.diary_type === SchoolDiaryType.BY_SUBJECT}
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setInitialValues({
                ...initialValues,
                diary_type: SchoolDiaryType.BY_SUBJECT,
              });
            }
          }}
        >
          Por asignatura
        </Checkbox>
      </Form.Item>

      {/* SCRUM-907: neighbour of the school diary because it decides who gets
          a diary at all. Off by default and scoped to this school. Driven by
          state, not by the Form store: edit-mode hydration is async, and a
          Form.Item name= would keep rendering the seed value after the
          backend's answer landed. */}
      <Form.Item style={{ marginBottom: 8 }}>
        <Checkbox
          checked={initialValues.primaria_diary_enabled ?? false}
          onChange={(e: CheckboxChangeEvent) =>
            setInitialValues({
              ...initialValues,
              primaria_diary_enabled: e.target.checked,
            })
          }
        >
          Bitácora de Primaria
        </Checkbox>
        <div>
          <Text type="secondary">
            Activar la bitácora digital para maestros y coordinadores de
            Primaria.
          </Text>
        </div>
      </Form.Item>

      <Form.Item name="demo" valuePropName="checked">
        <Checkbox
          value={initialValues.demo}
          onChange={(e: CheckboxChangeEvent) =>
            setInitialValues({
              ...initialValues,
              demo: e.target.checked,
            })
          }
        >
          Escuela Demo
        </Checkbox>
      </Form.Item>
        </>
      )}

      {/* Coordinador/Director: creation-only per product decision — once the
          school exists, this section is never shown again (no read-only
          display, no fetch). */}
      {!isUpdate && (
        <>
          <Title level={5} style={{ marginTop: 15, marginBottom: 15 }}>
            Coordinador / Director principal
          </Title>

          <Form.Item
            label="Nombre del coordinador"
            htmlFor="coordinator-first-name"
          >
            <Input
              id="coordinator-first-name"
              aria-label="Nombre del coordinador"
              placeholder="Nombre del coordinador"
              value={coordinator?.first_name ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleCoordinatorChange("first_name", e.target.value)
              }
            />
          </Form.Item>

          <Form.Item label="Apellido del coordinador" htmlFor="coordinator-last-name">
            <Input
              id="coordinator-last-name"
              aria-label="Apellido del coordinador"
              placeholder="Apellido del coordinador"
              value={coordinator?.last_name ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleCoordinatorChange("last_name", e.target.value)
              }
            />
          </Form.Item>

          <Form.Item label="Correo del coordinador" htmlFor="coordinator-email">
            <Input
              id="coordinator-email"
              aria-label="Correo del coordinador"
              placeholder="Correo del coordinador"
              type="email"
              status={coordinatorEmailInvalid ? "error" : undefined}
              value={coordinator?.email ?? ""}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleCoordinatorChange("email", e.target.value)
              }
              onBlur={() => setCoordinatorEmailTouched(true)}
            />
            {coordinatorEmailInvalid && (
              <div style={{ color: "#ff4d4f", marginTop: 4 }}>
                Ingresá un correo electrónico válido.
              </div>
            )}
          </Form.Item>
        </>
      )}

      {/* Programas: edit-mode only, replaces the dedicated Programas tab.
          Adding is additive/safe; removing is guarded by a confirmation
          inside SchoolProgramsSelect naming the exact class count. */}
      {isUpdate && !isCampus && (
        <Form.Item label="Programas">
          <SchoolProgramsSelect
            licensedProgramIds={licensedProgramIds}
            classes={classes}
            onCommitPrograms={onCommitPrograms}
          />
        </Form.Item>
      )}

      {/* SCRUM-795: van después de Programas porque aplican sobre los programas
          licenciados de la escuela. SCRUM-922: agrupados por nivel, y sólo los
          niveles que la escuela tiene. SCRUM-916: el bloque entero desaparece
          en un plantel — los cuatro interruptores se heredan de la principal. */}
      {!isCampus && (
      <ManipulativesSwitches
        values={{
          kinder_math_without_manipulatives:
            initialValues.kinder_math_without_manipulatives ?? false,
          kinder_literacy_without_manipulatives:
            initialValues.kinder_literacy_without_manipulatives ?? false,
          primaria_math_without_manipulatives:
            initialValues.primaria_math_without_manipulatives ?? false,
          primaria_literacy_without_manipulatives:
            initialValues.primaria_literacy_without_manipulatives ?? false,
        }}
        showKinder={hasKinder}
        showPrimaria={hasPrimaria}
        onChange={(field, checked) =>
          setInitialValues({ ...initialValues, [field]: checked })
        }
      />
      )}

      {children}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          {isUpdate ? "Actualizar Escuela" : "Guardar Escuela"}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default SchoolTab;
